import {
  ApiIncrementalityControlGroupType,
  ApiIncrementalityTestStatus,
  ApiIncrementalityTestType,
} from "@incendium/api";
import moment, { Moment } from "moment";

// Period labels & descriptions
export const TEST_PERIOD_LABEL = "Test period";
export const CONTROL_PERIOD_LABEL = "Control period";
export const CONTROL_PERIOD_ALIGNMENT_LABEL = "Control period alignment";

export const AUTO_MATCH_BASELINE_DAYS = 90;

export const CONTROL_PERIOD_DESCRIPTION =
  "The normal period before your holdout, while campaigns run as usual. After the test, we use how the test region and control group performed here to measure incremental impact.";

export const TEST_PERIOD_DESCRIPTION =
  "When you run the holdout in the test region during this window (for example, pausing or reducing campaigns there).";

export const DATES_SECTION_SUBTITLE =
  "Choose the control period (normal baseline) and test period (holdout) before launching.";

export const PERFORMANCE_CHART_SUBTITLE =
  "Indexed to control period mean (=100)";

export const PERFORMANCE_CHART_FOOTNOTE =
  "The test region underperformed the control group after the test started.";

export const TEST_PERIOD_STARTS_LABEL = "Test period starts";

// Test type
export const GEO_SWITCH_OFF_LABEL = "Geo Switch-Off";
export const GEO_ROLLOUT_LABEL = "Geo Rollout";
export const GEO_SWITCH_OFF_DESCRIPTION =
  "Reduce or pause campaigns in a region.";
export const GEO_SWITCH_OFF_SUMMARY_DESCRIPTION =
  "Pause campaigns in test region";
export const GEO_SWITCH_OFF_REVIEW_DESCRIPTION =
  "Pause campaigns in the test region.";
export const GEO_ROLLOUT_DESCRIPTION =
  "Increase or launch campaigns in a region.";
export const WHY_THIS_TEST_DESCRIPTION =
  "Compare performance in the test region to similar regions to isolate true causal impact.";

export const WHY_GEO_TESTS_TITLE = "Why geo tests work";
export const WHY_GEO_TESTS_BULLETS = [
  "- Real-world behaviour",
  "- Controls for external factors",
  "- Delivers causal insights",
] as const;

// Region & control
export const TEST_REGION_LABEL = "Test region";
export const CONTROL_GROUP_LABEL = "Control group";
export const TEST_NAME_LABEL = "Test name";
export const TEST_SUMMARY_TITLE = "Test summary";
export const TEST_TYPE_ACTION_LABEL = "Test type & action";

export const AUTO_MATCHED_CONTROL_LABEL = "Auto-matched control";
export const AUTO_MATCHED_REGIONS_LABEL = "Auto-matched regions";
export const AUTO_MATCHED_REGIONS_RECOMMENDED_LABEL =
  "Auto-matched regions (recommended)";
export const CUSTOM_REGIONS_LABEL = "Custom regions";

export const COMING_SOON_LABEL = "Coming soon";
export const RECOMMENDED_CHIP_LABEL = "Recommended";
export const SELECTED_CHIP_LABEL = "Selected";

// Control group preview
export const CONTROL_GROUP_PREVIEW_TITLE = "Control group match preview";
export const MATCHED_REGIONS_LABEL = "Matched regions";
export const OVERALL_SIMILARITY_SCORE_LABEL = "Overall similarity score";
export const SIMILARITY_BASELINE_DESCRIPTION = `Based on the last ${AUTO_MATCH_BASELINE_DAYS} days of performance`;
export const SIMILARITY_CRITERIA = [
  "Traffic Volume",
  "Conversion Rate",
  "Channel mix",
  "Trend alignment",
] as const;

export const CONTROL_GROUP_QUALITY_LABEL = "Control group quality";
export const OVERALL_SIMILARITY_SCORE_PREFIX = "Overall similarity score:";

// Review & launch
export const REVIEW_READY_TITLE = "Your test is ready to launch";
export const REVIEW_READY_SUBTITLE =
  "Review details below. You can go back to make changes.";
export const REQUIRED_BEFORE_LAUNCH_LABEL = "Required before launch";
export const WHY_THIS_TEST_LABEL = "Why this test";

// Stepper
export const SETUP_STEP_LABELS = {
  design: { title: "Test Design", subtitle: "Define test and control" },
  metrics: { title: "Metrics", subtitle: "Choose KPI's" },
  review: { title: "Review", subtitle: "Confirm and create" },
  results: { title: "Results", subtitle: "View test impact" },
} as const;

// Setup sections
export const TEST_TYPE_SECTION_TITLE = "1. Test type and action";
export const TEST_TYPE_SECTION_SUBTITLE =
  "Choose how you want to change activity in your test region.";
export const TEST_NAME_PLACEHOLDER = "e.g. UK Geo Switch-Off - London";
export const TEST_NAME_REQUIRED_ERROR = "Test name is required";

export const DATES_SECTION_TITLE = "2. Select test dates";
export const REGION_SECTION_TITLE = "3. Define test region and control";
export const REGION_SECTION_SUBTITLE =
  "Select the test region and how we build your control group.";

export const PERFORMANCE_OVER_TIME_TITLE = "Performance over time";

// Metrics tab
export const METRICS_PRIMARY_INFO =
  "Your primary metric is used to calculate incremental impact. Supporting metrics help explain what is driving the change.";
export const METRICS_PRIMARY_SECTION_TITLE = "3. Select primary metric";
export const METRICS_PRIMARY_SECTION_SUBTITLE =
  "This is the main metric used to calculate incremental impact.";
export const METRICS_SUPPORTING_SECTION_TITLE =
  "4. Select supporting metrics (up to 6)";
export const METRICS_SUPPORTING_SECTION_SUBTITLE =
  "These help explain the drivers behind the change.";

// Test list
export const TEST_STATUS_LABELS: Record<ApiIncrementalityTestStatus, string> = {
  [ApiIncrementalityTestStatus.INCREMENTALITY_TEST_STATUS_UNSPECIFIED]:
    "Unspecified",
  [ApiIncrementalityTestStatus.INCREMENTALITY_TEST_STATUS_DRAFT]: "Draft",
  [ApiIncrementalityTestStatus.INCREMENTALITY_TEST_STATUS_ACTIVE]: "Active",
};

export const DELETE_TEST_CONFIRM_TITLE =
  "Are you sure you want to delete this test";
export const DELETE_TEST_CONFIRM_BODY = "This action cannot be undone.";

// Validation & notifications
export const VALIDATION_MESSAGES = {
  testName: "Please enter a test name",
  testRegion: "Please select a test region",
  testDates: "Please select a test date range",
  controlPeriodDates: "Please select a control period date range",
  primaryMetric: "Please select a primary metric",
  supportingMetrics: "Please select at least one supporting metric",
} as const;

export const NOTIFICATION_MESSAGES = {
  loadFailed: "Failed to load incrementality test",
  saveFailed: "Failed to save incrementality test",
  updated: "Incrementality test updated",
  created: "Incrementality test created",
} as const;

export const DATE_VALIDATION_MESSAGES = {
  testFromBeforeTo: "Test period 'From' must be on or before 'To'.",
  controlFromBeforeTo: "Control period 'From' must be on or before 'To'.",
  controlBeforeTest:
    "Control period must end before the test period starts.",
} as const;

const DEFAULT_TEST_REGION_FALLBACK = "your selected test region";
const REVIEW_TEST_REGION_FALLBACK = "the selected test region";
const DEFAULT_TEST_START = "the test start date";
const DEFAULT_TEST_END = "the test end date";
const DEFAULT_COUNTRY = "country";
const DEFAULT_CITY = "this city";
const DEFAULT_SELECTED_COUNTRY = "the selected country";

export function formatTestRegionLabel(
  city?: string,
  country?: string,
  fallback = DEFAULT_TEST_REGION_FALLBACK,
): string {
  const label = [city, country].filter(Boolean).join(", ");
  return label || fallback;
}

export function formatReviewDateRange(
  from: Moment | null,
  to: Moment | null,
): string {
  if (!from || !to) return "-";
  return `${from.format("MMM D")} - ${to.format("MMM D, YYYY")}`;
}

export function getControlGroupLabel(
  controlGroupType: ApiIncrementalityControlGroupType | undefined,
  options: { testRegionCity?: string; testRegionCountry?: string },
): string {
  if (
    controlGroupType ===
    ApiIncrementalityControlGroupType.INCREMENTALITY_CONTROL_GROUP_TYPE_AUTO_MATCHED
  ) {
    return AUTO_MATCHED_REGIONS_LABEL;
  }
  if (
    controlGroupType ===
    ApiIncrementalityControlGroupType.INCREMENTALITY_CONTROL_GROUP_TYPE_REST_OF_COUNTRY
  ) {
    return `Rest of ${options.testRegionCountry || DEFAULT_COUNTRY}`;
  }
  const citySuffix = options.testRegionCity
    ? ` (match ${options.testRegionCity})`
    : "";
  return `Custom regions${citySuffix}`;
}

export function getDateValidationMessages(options: {
  testFrom?: Moment | null;
  testTo?: Moment | null;
  preTestFrom?: Moment | null;
  preTestTo?: Moment | null;
}): string[] {
  const { testFrom, testTo, preTestFrom, preTestTo } = options;
  const messages: string[] = [];
  if (testFrom && testTo && testFrom.isAfter(testTo, "day")) {
    messages.push(DATE_VALIDATION_MESSAGES.testFromBeforeTo);
  }
  if (preTestFrom && preTestTo && preTestFrom.isAfter(preTestTo, "day")) {
    messages.push(DATE_VALIDATION_MESSAGES.controlFromBeforeTo);
  }
  if (preTestTo && testFrom && !preTestTo.isBefore(testFrom, "day")) {
    messages.push(DATE_VALIDATION_MESSAGES.controlBeforeTest);
  }
  return messages;
}

export function buildSetupHoldoutNotice(options: {
  type?: ApiIncrementalityTestType;
  testRegionCity?: string;
  testRegionCountry?: string;
  testFrom?: Date | string;
  testTo?: Date | string;
}): string {
  const region = formatTestRegionLabel(
    options.testRegionCity,
    options.testRegionCountry,
  );
  const testFrom = options.testFrom
    ? moment(options.testFrom).format("D MMM YYYY")
    : DEFAULT_TEST_START;
  const testTo = options.testTo
    ? moment(options.testTo).format("D MMM YYYY")
    : DEFAULT_TEST_END;

  if (
    options.type ===
    ApiIncrementalityTestType.INCREMENTALITY_TEST_TYPE_GEO_SWITCH_OFF
  ) {
    return `This is a holdout test. You need to pause or reduce ads in ${region} from ${testFrom} to ${testTo}. Incendium does not switch campaigns on or off for you.`;
  }

  return `You are responsible for applying campaign changes in ${region} from ${testFrom} to ${testTo}. Incendium does not make these changes automatically.`;
}

export function buildReviewHoldoutNotice(options: {
  testRegionCity?: string;
  testRegionCountry?: string;
  testFrom?: Date | string;
  testTo?: Date | string;
}): string {
  const region = formatTestRegionLabel(
    options.testRegionCity,
    options.testRegionCountry,
    REVIEW_TEST_REGION_FALLBACK,
  );
  const period =
    options.testFrom && options.testTo
      ? formatReviewDateRange(
          moment(options.testFrom),
          moment(options.testTo),
        )
      : "the test period";

  return `Action required: You must pause or reduce ads in ${region} during ${period}. Incendium does not apply campaign changes for you.`;
}

export function buildTestSeriesLabel(testRegionCity?: string): string {
  return testRegionCity
    ? `${testRegionCity} (test region)`
    : "Test region";
}

export function buildAutoMatchedControlDescription(testRegionCity?: string): string {
  return `We'll automatically select regions that behave most like ${testRegionCity || DEFAULT_CITY} using the last ${AUTO_MATCH_BASELINE_DAYS} days of performance.`;
}

export function buildRestOfCountryControlDescription(
  testRegionCountry?: string,
): string {
  return `Use all other regions in ${testRegionCountry || DEFAULT_SELECTED_COUNTRY} as your control group.`;
}

export function buildCustomRegionsControlDescription(
  testRegionCity?: string,
): string {
  return `Choose specific regions to match ${testRegionCity || "the selected city"}.`;
}

export function buildResultInsightCopy(options: {
  impactPercentage?: number;
  testCity: string;
  metricLabel: string;
}): string {
  const { impactPercentage, testCity, metricLabel } = options;
  const metric = metricLabel.toLowerCase();

  if (impactPercentage == null) {
    return "Result insight will appear once calculation is complete.";
  }
  if (impactPercentage < 0) {
    return `When campaigns were paused in ${testCity}, ${metric} fell ${Math.abs(impactPercentage).toFixed(1)}% more than expected compared to similar regions.`;
  }
  if (impactPercentage > 0) {
    return `When campaigns were paused in ${testCity}, ${metric} increased ${Math.abs(impactPercentage).toFixed(1)}% more than expected compared to similar regions.`;
  }
  return `When campaigns were paused in ${testCity}, ${metric} performed in line with expected compared to similar regions.`;
}
